import { useState, useEffect } from "react";
import { X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { formatApiError } from "@/lib/api-error";
import { useQueryClient } from "@tanstack/react-query";
import type { UserProfile, ScreenType } from "@/types/user-profile";
import { allScreens, screenLabels } from "@/types/user-profile";

interface ProfilePermissionsModalProps {
  profile: UserProfile | null;
  onClose: () => void;
}

export function ProfilePermissionsModal({
  profile,
  onClose,
}: ProfilePermissionsModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [allowedScreens, setAllowedScreens] = useState<ScreenType[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize state when profile changes
  useEffect(() => {
    if (profile) {
      setAllowedScreens(profile.allowed_screens || []);
      setHasChanges(false);
    }
  }, [profile]);

  if (!profile) return null;

  const isScreenAllowed = (screen: ScreenType) => {
    return allowedScreens.includes(screen);
  };

  const handleToggleScreen = (screen: ScreenType) => {
    setAllowedScreens((prev) => {
      const isCurrentlyAllowed = prev.includes(screen);
      const newScreens = isCurrentlyAllowed
        ? prev.filter((s) => s !== screen)
        : [...prev, screen];
      
      // Check if there are changes compared to original
      const originalSet = new Set(profile.allowed_screens);
      const newSet = new Set(newScreens);
      const hasChanged = 
        originalSet.size !== newSet.size ||
        [...originalSet].some((s) => !newSet.has(s));
      
      setHasChanges(hasChanged);
      return newScreens;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Write em user_profiles vai pela API (a migration 133 trancou a tabela
      // no /rest/v1).
      await apiFetch(`${SUPABASE_URL}admin/perfis/${profile.id}/permissoes`, {
        method: "PUT",
        body: JSON.stringify({ allowed_screens: allowedScreens }),
      });

      toast({
        title: "Permissões atualizadas",
        description: `As permissões do perfil "${profile.name}" foram atualizadas com sucesso.`,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["user_profiles"] });
      queryClient.invalidateQueries({ queryKey: ["users_with_profiles"] });

      setHasChanges(false);
      onClose();
    } catch (error: unknown) {
      console.error("Erro ao atualizar permissões:", error);
      const { detail } = formatApiError(error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar as permissões. " + detail,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setAllowedScreens(profile.allowed_screens || []);
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog open={!!profile} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              Permissões: {profile.name}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {profile.description}
          </p>
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Telas Permitidas</h4>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {allScreens.map((screen) => (
                <div
                  key={screen}
                  className="flex items-center justify-between space-x-2 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                >
                  <Label 
                    htmlFor={screen} 
                    className="flex-1 cursor-pointer"
                    onClick={() => handleToggleScreen(screen)}
                  >
                    {screenLabels[screen]}
                  </Label>
                  <Switch
                    id={screen}
                    checked={isScreenAllowed(screen)}
                    onCheckedChange={() => handleToggleScreen(screen)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              "Salvando..."
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvar Alterações
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
